import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Core variants
        default: "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-sm",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        
        // Extended variants
        success: "bg-emerald-600 text-primary-foreground hover:bg-emerald-700 shadow-sm dark:bg-emerald-700 dark:hover:bg-emerald-600",
        warning: "bg-amber-500 text-primary-foreground hover:bg-amber-600 shadow-sm dark:bg-amber-600 dark:hover:bg-amber-500",
        info: "bg-sky-500 text-primary-foreground hover:bg-sky-600 shadow-sm dark:bg-sky-600 dark:hover:bg-sky-500",
        
        // Soft/muted variants (colored backgrounds with matching text)
        "soft-primary": "bg-primary/10 text-primary hover:bg-primary/20",
        "soft-destructive": "bg-destructive/10 text-destructive hover:bg-destructive/20",
        "soft-success": "bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 dark:text-emerald-400",
        "soft-warning": "bg-amber-500/10 text-amber-600 hover:bg-amber-500/20 dark:text-amber-400",
        "soft-info": "bg-sky-500/10 text-sky-600 hover:bg-sky-500/20 dark:text-sky-400",
        
        // Outline variants with colors
        "outline-primary": "border-2 border-primary text-primary bg-transparent hover:bg-primary hover:text-primary-foreground",
        "outline-destructive": "border-2 border-destructive text-destructive bg-transparent hover:bg-destructive hover:text-destructive-foreground",
        "outline-success": "border-2 border-emerald-600 text-emerald-600 bg-transparent hover:bg-emerald-600 hover:text-primary-foreground dark:border-emerald-500 dark:text-emerald-500",
        
        // Premium/gradient variant
        premium: "bg-gradient-to-r from-primary to-accent text-primary-foreground hover:opacity-90 shadow-md",
      },
      size: {
        default: "h-10 px-4 py-2",
        xs: "h-7 rounded px-2 text-xs",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        xl: "h-12 rounded-lg px-10 text-base",
        icon: "h-10 w-10",
        "icon-sm": "h-8 w-8",
        "icon-lg": "h-12 w-12",
      },
      rounded: {
        default: "",
        full: "rounded-full",
        none: "rounded-none",
      },
      fullWidth: {
        true: "w-full",
        false: "",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      rounded: "default",
      fullWidth: false,
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, rounded, fullWidth, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, rounded, fullWidth, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
